import React, { useState, useEffect }  from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { DateTime } from 'luxon';
import { Link as RouterLink } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Switch from '@material-ui/core/Switch';
import FormLabel from '@material-ui/core/FormLabel';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import Checkbox from '@material-ui/core/Checkbox';
import Collapse from '@material-ui/core/Collapse';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Chip from '@material-ui/core/Chip';

import AcceptPaymentIcon from '@material-ui/icons/AttachMoney';
import DeleteIcon from '@material-ui/icons/Delete';

import Loading from 'app/components/loaders/Loading';
import SaveButton from 'app/components/buttons/SaveButton';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';
import useTerminal from 'app/context/useTerminal';

import States from 'app/utils/States';
import HandleResponse from 'app/utils/HandleResponse';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    invoiceItems: {
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: theme.palette.divider,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(3)
    },
    invoiceItem: {
        padding: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    success: {
        backgroundColor: theme.palette.success.main,
        color: "#fff"
    },
    warning: {
        backgroundColor: theme.palette.warning.main,
        color: "#fff"
    },
    greyBG: {
        backgroundColor: theme.palette.grey[200],
    },
    whiteBG: {
        backgroundColor: '#fff',
    }
}));

const newInvoiceItem = {
    name: "",
    description: "",
    quantity: 1,
    amount: 0.00
}

const initSettings = {
    payment_methods: [],
    display_donation_field: false,
    check_name: '',
    check_address_one: '',
    check_address_two: '',
    check_city: '',
    check_state: 'AL',
    check_zip: '',
    stripe_account_id: null
}

const initMISettings = {
    ml_auto_invoice: true,
    ml_auto_late_fees: true,
    ml_include_convenience_fees: false,
    ml_send_invoice_emails: true,
    ml_send_late_emails: true,
    ml_invoice_prefix: "",
    ml_invoice_email_sent_at: null,
    ml_late_email_sent_at: null,
}

export default function Settings() {

    const [ membershipInvoiceSettings, setMembershipInvoiceSettings ] = useState(initMISettings);

    const [ settings, setSettings ] = useState(initSettings);
    const [ invoiceItems, setInvoiceItems ] = useState([]);
    const [ readers, setReaders ] = useState([]);
    const [ selectedReader, setSelectedReader ] = useState(null);
    const [ connectedReader, setConnectedReader ] = useState(null);

    const [ settingsLoading, setSettingsLoading ] = useState(false);
    const [ miSettingsLoading, setMISettingsLoading ] = useState(false);
    const [ terminalSettingsLoading, setTerminalSettingsLoading ] = useState(false);

    const [ errors, setErrors ] = useState({});

    const classes = useStyles();
    const api = useApi();
    const { app } = useApp();
    const { terminal, loadTerminal } = useTerminal();
    const { enqueueSnackbar } = useSnackbar();

    // GETTERS ------------------------------------------------

    const getSettings = () => {
        setSettingsLoading(true);

        api.get('/admin/accounting/settings/general')
            .then((res) => setSettings(HandleResponse(res.settings, initSettings)))
            .finally(() => setSettingsLoading(false));
    }

    const getMembershipInvoiceSettings = () => {
        setMISettingsLoading(true);

        api.get('/admin/accounting/settings/membership/invoices')
            .then((res) => {
                setMembershipInvoiceSettings(HandleResponse(res.settings, initMISettings));

                if (res.ml_invoice_items && res.ml_invoice_items != "") {
                    setInvoiceItems(res.ml_invoice_items);
                }
            }).finally(() => setMISettingsLoading(false));
    }

    useEffect(() => {
        getSettings();
        getMembershipInvoiceSettings();
    }, [])

    useEffect(() => {
        if (settings.stripe_account_id !== null && settings.stripe_account_id !== '') {
            loadTerminal();
        }
    }, [settings]);

    useEffect(() => {
        console.log(terminal);
        if (terminal) discoverReaders();
    }, [terminal]);

    // SETTERS ------------------------------------------------

    const handleMIChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setMembershipInvoiceSettings({ ...membershipInvoiceSettings, [name]: value });
    };

    const handleSettingsChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setSettings({ ...settings, [name]: value });
    };

    const handlePaymentMethodsChange = event => {
        const name = event.target.name;
        const newSettings = {...settings};
        const i = newSettings.payment_methods.indexOf(name);

        if (isError('payment_methods')) delete errors.payment_methods;

        if (i > -1) {
            newSettings.payment_methods.splice(i,1);
        } else {
            newSettings.payment_methods.push(name);
        }

        setSettings(newSettings);
    };

    const handleItemChange = (event, name, i) => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isInvoiceItemError(name)) delete errors.invoice_items[i][name];

        const newItems = [...invoiceItems];
        const newItem = {...invoiceItems[i]};
        newItem[name] = value;
        newItems[i] = newItem;
        setInvoiceItems(newItems);
    };

    const createInvoiceItem = () => {
        const newItems = [...invoiceItems];
        newItems.push(newInvoiceItem);
        setInvoiceItems(newItems)
    }

    const removeInvoiceItem = i => {
        const newItems = [...invoiceItems];
        newItems.splice(i, 1);
        setInvoiceItems(newItems)
    }

    const discoverReaders = () => {

        if (terminal) {

            setTerminalSettingsLoading(true);

            terminal.discoverReaders({simulated: (process.env.MIX_SIMULATED_TERMINAL === 'true')}).then(function(discoverResult) {

                let error = null;

                if (discoverResult.error) {
                    error = discoverResult.error;
                } else if (discoverResult.discoveredReaders.length === 0) {
                    error = 'No Available Readers';
                } else {
                    setReaders(discoverResult.discoveredReaders);

                }

                if (error) {
                    enqueueSnackbar(
                        error, {
                            variant: 'warning',
                            autoHideDuration: 5000,
                            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                        }
                    );
                }
            }).finally(() => setTerminalSettingsLoading(false));
        } else {
            enqueueSnackbar(
                "Stripe Terminal has not yet loaded. Please try again in a few moments.", {
                    variant: 'warning',
                    autoHideDuration: 5000,
                    anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                }
            );
        }
    }

    const connectReader = () => {
        // Just select the first reader here.
        if (selectedReader) {

            setTerminalSettingsLoading(true);

            terminal.connectReader(selectedReader).then(function(connectResult) {
                if (connectResult.error) {
                    enqueueSnackbar(
                        connectResult.error, {
                            variant: 'warning',
                            autoHideDuration: 5000,
                            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
                        }
                    );
                } else {
                    console.log('Connected to reader: ', connectResult);
                }
            }).finally(() => setTerminalSettingsLoading(false));
        }
    }

    // POSTERS ------------------------------------------------

    const postMembershipInvoiceSettings = () => {
        setMISettingsLoading(true);

        const data = {...membershipInvoiceSettings, ml_invoice_items: invoiceItems }

        api.post('/admin/accounting/settings/membership/invoices', data)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).then(() => setMISettingsLoading(false));
    }

    const postSettings = () => {
        setSettingsLoading(true);

        api.post('/admin/accounting/settings/general', settings)
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).then(() => setSettingsLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const isInvoiceItemError = (field, i) => {
        if (errors.invoice_items && errors.invoice_items[i] && errors.invoice_items[i][field]) return true;
        return false;
    }

    const getInvoiceItemErrorMessage = (field, i) => {
        if (isInvoiceItemError(field)) return errors.invoice_items[i][field];
        return "";
    }

    return (
        <form noValidate autoComplete="off" className={classes.container}>
            <Paper className={classes.paper}>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Accounting Settings
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary">
                    Welcome to your accounting settings. Here you can modify how your membership invoicing works. Membership invoicing is set on a per Membership Level basis on the Membership Levels Administrative page.
                </Typography>
            </Paper>
            <Paper className={classes.paper}>
                <Loading isLoading={settingsLoading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                           General Settings
                        </Typography>
                    </Grid>
                </Grid>
                <FormControl
                    component="fieldset"
                    error={isError('payment_methods')}
                    margin="normal"
                    >
                    <FormLabel component="legend">Available Payment Methods</FormLabel>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={(settings.payment_methods.indexOf('stripe') > -1)}
                                    onChange={handlePaymentMethodsChange}
                                    name="stripe"
                                    color="primary"
                                    disabled={(settings.stripe_account_id == null || settings.stripe_account_id === '')}
                                />
                            }
                            label={
                                <div>
                                    <Typography variant="body1" display="inline">
                                        Credit Card
                                    </Typography>
                                    {(settings.stripe_account_id == null || settings.stripe_account_id === '') &&
                                        <>
                                            <Typography color="error" display="inline">
                                                &nbsp;&nbsp;&nbsp;Must enable online payments first.&nbsp;&nbsp;&nbsp;
                                                <Link
                                                    aria-label="Enable Online Payments"
                                                    component={RouterLink}
                                                    to={"/admin/accounting/stripe/onboarding"}
                                                    >
                                                    Enable Online Payments
                                                </Link>
                                            </Typography>
                                        </>
                                    }
                                </div>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={(settings.payment_methods.indexOf('stripe_ach') > -1)}
                                    onChange={handlePaymentMethodsChange}
                                    name="stripe_ach"
                                    color="primary"
                                    disabled={(settings.stripe_account_id == null || settings.stripe_account_id === '')}
                                />
                            }
                            label={
                                <div>
                                    <Typography variant="body1" display="inline">
                                        ACH Payments
                                    </Typography>
                                    {(settings.stripe_account_id == null || settings.stripe_account_id === '') &&
                                        <>
                                            <Typography color="error" display="inline">
                                                &nbsp;&nbsp;&nbsp;Must enable online payments first.&nbsp;&nbsp;&nbsp;
                                                <Link
                                                    aria-label="Enable Online Payments"
                                                    component={RouterLink}
                                                    to={"/admin/accounting/stripe/onboarding"}
                                                    >
                                                    Enable Online Payments
                                                </Link>
                                            </Typography>
                                        </>
                                    }
                                </div>
                            }
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={(settings.payment_methods.indexOf('check') > -1)}
                                    onChange={handlePaymentMethodsChange}
                                    name="check"
                                    color="primary"
                                />
                            }
                            label="Check"
                        />
                    </FormGroup>
                    {isError('payment_methods') &&
                        <FormHelperText>
                            {getErrorMessage('payment_methods')}
                        </FormHelperText>
                    }
                </FormControl>
                <Collapse in={(settings.payment_methods.indexOf('check') > -1)}>
                    <Grid container spacing={3} className={classes.marginTop}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">
                                Enter the address for users to send checks:
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                id="input-check-name"
                                label="Check Addressed To"
                                value={settings.check_name}
                                onChange={handleSettingsChange('check_name')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_name')}
                                helperText={getErrorMessage('check_name')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                id="input-address-one"
                                label="Address One"
                                value={settings.check_address_one}
                                onChange={handleSettingsChange('check_address_one')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_address_one')}
                                helperText={getErrorMessage('check_address_one')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={12} lg={6}>
                            <TextField
                                id="input-address-two"
                                label="Address Two"
                                value={settings.check_address_two}
                                onChange={handleSettingsChange('check_address_two')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_address_two')}
                                helperText={getErrorMessage('check_address_two')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5} md={12} lg={6}>
                            <TextField
                                id="input-city"
                                label="City"
                                value={settings.check_city}
                                onChange={handleSettingsChange('check_city')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_city')}
                                helperText={getErrorMessage('check_city')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={12} lg={2}>
                            <TextField
                                id="input-state"
                                select
                                label="State"
                                value={settings.check_state}
                                onChange={handleSettingsChange('check_state')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_state')}
                                helperText={getErrorMessage('check_state')}
                                >
                                {States.map( state => {
                                    return (
                                        <MenuItem
                                            key={state.abbreviation}
                                            value={state.abbreviation}
                                            >
                                            {state.name}
                                        </MenuItem>
                                    )
                                })}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3} md={12} lg={4}>
                            <TextField
                                id="input-zip"
                                label="ZIP"
                                value={settings.check_zip}
                                onChange={handleSettingsChange('check_zip')}
                                fullWidth
                                variant='outlined'
                                error={isError('check_zip')}
                                helperText={getErrorMessage('check_zip')}
                            />
                        </Grid>
                    </Grid>
                </Collapse>
                <FormControlLabel
                    className={classes.marginTop}
                    control={
                        <Switch
                            checked={settings.display_donation_field}
                            onChange={handleSettingsChange("display_donation_field")}
                            name="display_donation_field"
                            color="primary"
                            disabled={(settings.payment_methods.indexOf('stripe') === -1)}
                        />
                    }
                    label="Display the donation box when users are paying invoices?"
                    margin="normal"
                />
                {(settings.payment_methods.indexOf('stripe') === -1) &&
                    <FormHelperText>Must enable Credit Card payments first.</FormHelperText>
                }
                <Grid
                    container
                    spacing={3}
                    justifyContent="space-between"
                    alignItems="flex-end"
                    className={classes.marginTop}
                    >
                    <Grid item>
                        {(settings.stripe_account_id == null || settings.stripe_account_id === '') ? (
                            <>
                                <Tooltip
                                    title="Start accepting payments online in a few easy steps."
                                    disableFocusListener
                                    >
                                    <Button
                                        aria-label="Enable Online Payments"
                                        component={RouterLink}
                                        to={"/admin/accounting/stripe/onboarding"}
                                        startIcon={<AcceptPaymentIcon />}
                                        color="primary"
                                        variant="outlined"
                                        >
                                        Enable Online Payments
                                    </Button>
                                </Tooltip>
                                <br/>
                                <Typography variant="subtitle" className={classes.marginTop} component="div">
                                    Already have a Stripe account?&nbsp;
                                    <Link
                                        target="_blank"
                                        href={`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=ca_FSuEEVJqfij56l8qO5hepD9EqHSIwnuS&scope=read_write&state=${app.club.id}`}
                                        >
                                        Connect Your Account
                                    </Link>.
                                </Typography>
                            </>
                            
                        ) : (
                            <Typography
                                variant="subtitle2"
                                color="primary"
                                >
                                Your organization can accept Credit Card payments.
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        <SaveButton
                            disabled={settingsLoading}
                            variant="contained"
                            color="primary"
                            onClick={postSettings}
                            >
                            Update
                        </SaveButton>
                    </Grid>
                </Grid>
            </Paper>
            {(settings.stripe_account_id !== null && settings.stripe_account_id !== '') &&
                <Paper className={classes.paper}>
                    <Loading isLoading={terminalSettingsLoading}/>
                    <Grid container
                        alignItems="center"
                        justify="space-between"
                        spacing={3}
                        >
                        <Grid item>
                            <Typography variant="h6" component="h3">
                                Terminal Settings
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                aria-label="Refresh Available Readers"
                                color="primary"
                                variant="outlined"
                                onClick={discoverReaders}
                                >
                                Refresh Available Readers
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} className={classes.marginTop}>
                        <Grid item xs={6} className={classes.greyBG}>
                            {(readers.length > 0) ? (
                                <List>
                                    {readers.map(reader => (
                                        <ListItem
                                            button
                                            key={reader.id}
                                            onClick={() => setSelectedReader(reader)}
                                            selected={(selectedReader && reader.id === selectedReader.id)}
                                            className={classes.whiteBG}
                                            >
                                            <Grid container justify="space-between" spacing={3}>
                                                <Grid item>
                                                    <Typography color="primary" variant="subtitle1">{reader.label}</Typography>
                                                    <Typography color="textSecondary" variant="subtitle2">
                                                        {`IP Address: ${reader.ip_address} | Serial Number: ${reader.serial_number}`}
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Chip
                                                        className={(reader.status === 'online') ? classes.success : classes.warning}
                                                        label={reader.status}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="subtitle2" component="h5">
                                    No Available Terminals
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={6} className={classes.greyBG}>
                            {selectedReader &&
                                <Paper className={classes.paper}>
                                    <Typography variant="h6" component="h5" color="primary" gutterBottom>
                                        {selectedReader.label}
                                    </Typography>
                                    <Grid container spacing={3} justify="space-between">
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                Device Type:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2"  color="textSecondary">
                                                {selectedReader.device_type}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3} justify="space-between">
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                Location:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2"  color="textSecondary">
                                                {selectedReader.location}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3} justify="space-between">
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                Serial Number:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2"  color="textSecondary">
                                                {selectedReader.serial_number}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Grid container spacing={3} justify="space-between">
                                        <Grid item>
                                            <Typography variant="subtitle2">
                                                IP Address:
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="body2"  color="textSecondary">
                                                {selectedReader.ip_address}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                    <Button
                                        aria-label="Connect to Reader"
                                        color="primary"
                                        variant="outlined"
                                        onClick={connectReader}
                                        className={classes.marginTop}
                                        >
                                        Connect
                                    </Button>
                                </Paper>
                            }
                        </Grid>
                    </Grid>
                </Paper>
            }
            <Paper className={classes.paper}>
                <Loading isLoading={miSettingsLoading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h3" gutterBottom>
                            Membership Invoice Settings
                        </Typography>
                    </Grid>
                </Grid>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={membershipInvoiceSettings.ml_auto_invoice}
                                    onChange={handleMIChange("ml_auto_invoice")}
                                    name="ml_auto_invoice"
                                    color="primary"
                                />
                            }
                            label="Toggle Auto Invoicing for Membership Invoices"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={membershipInvoiceSettings.ml_auto_late_fees}
                                    onChange={handleMIChange("ml_auto_late_fees")}
                                    name="ml_auto_late_fees"
                                    color="primary"
                                />
                            }
                            label="Toggle Auto Late Fees for Membership Invoices"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={membershipInvoiceSettings.ml_send_invoice_emails}
                                    onChange={handleMIChange("ml_send_invoice_emails")}
                                    name="ml_send_invoice_emails"
                                    color="primary"
                                />
                            }
                            label={
                                <div>
                                    Send invoice emails when Membership Levels are invoiced?<br/>
                                    <Typography display="inline" variant="subtitle2" color="textSecondary">
                                        Emails Last Sent:&nbsp;&nbsp;&nbsp;
                                    </Typography>
                                    <Typography display="inline" variant="subtitle2">
                                        {membershipInvoiceSettings.ml_invoice_email_sent_at ? DateTime.fromISO(membershipInvoiceSettings.ml_invoice_email_sent_at).toLocaleString(DateTime.DATETIME_MED) : 'Never'}
                                    </Typography>
                                </div>
                            }
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={membershipInvoiceSettings.ml_send_late_emails}
                                    onChange={handleMIChange("ml_send_late_emails")}
                                    name="ml_send_late_emails"
                                    color="primary"
                                />
                            }
                            label={
                                <div>
                                    Send late notices when Membership Levels are past due?<br/>
                                    <Typography display="inline" variant="subtitle2" color="textSecondary">
                                        Notices Last Sent:&nbsp;&nbsp;&nbsp;
                                    </Typography>
                                    <Typography display="inline" variant="subtitle2">
                                        {membershipInvoiceSettings.ml_late_email_sent_at ? DateTime.fromISO(membershipInvoiceSettings.ml_late_email_sent_at).toLocaleString(DateTime.DATETIME_MED) : 'Never'}
                                    </Typography>
                                </div>
                            }
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={membershipInvoiceSettings.ml_include_convenience_fees}
                                    onChange={handleMIChange("ml_include_convenience_fees")}
                                    name="ml_include_convenience_fees"
                                    color="primary"
                                />
                            }
                            label="Push convenience fees to Users?"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Invoice Number Prefix
                        </Typography>
                        <TextField
                            id="input-invoice-prefix"
                            value={membershipInvoiceSettings.ml_invoice_prefix}
                            onChange={handleMIChange('ml_invoice_prefix')}
                            error={isError('ml_invoice_prefix')}
                            helperText={getErrorMessage('ml_invoice_prefix')}
                            variant='outlined'
                        />
                    <Typography component="div" variant="caption" color="textSecondary">
                            Used to prefix the invoice number for internal reference
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" gutterBottom>
                            Add additional invoice items for membership invoices
                        </Typography>
                        <div className={classes.invoiceItems}>
                            {invoiceItems.map((item, i) => (
                                <Grid key={i} container spacing={3} alignItems="center" wrap="nowrap">
                                    <Grid item>
                                        <TextField
                                            label="Item Name"
                                            value={item.name}
                                            onChange={(e) => handleItemChange(e, 'name', i)}
                                            fullWidth
                                            error={isInvoiceItemError('name', i)}
                                            helperText={getInvoiceItemErrorMessage('name', i)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            label="Description"
                                            value={item.description}
                                            onChange={e => handleItemChange(e, 'description', i)}
                                            fullWidth
                                            error={isInvoiceItemError('description', i)}
                                            helperText={getInvoiceItemErrorMessage('description', i)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            label="Quantity"
                                            type="number"
                                            min="1"
                                            step="1"
                                            value={item.quantity}
                                            onChange={e => handleItemChange(e, 'quantity', i)}
                                            fullWidth
                                            error={isInvoiceItemError('quantity', i)}
                                            helperText={getInvoiceItemErrorMessage('quantity', i)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            label="Price"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={item.amount}
                                            onChange={e => handleItemChange(e, 'amount', i)}
                                            fullWidth
                                            error={isInvoiceItemError('amount', i)}
                                            helperText={getInvoiceItemErrorMessage('amount', i)}
                                            variant="outlined"
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item>
                                        ${(item.quantity * item.amount).toFixed(2)}
                                    </Grid>
                                    <Grid item>
                                        <IconButton onClick={() => removeInvoiceItem(i)}>
                                            <DeleteIcon/>
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        size="small"
                                        onClick={createInvoiceItem}
                                        >
                                        Add Invoice Item
                                    </Button>
                                </Grid>
                            </Grid>
                        </div>
                    </Grid>
                </Grid>
                <Typography
                    align="right"
                    component="div"
                    className={classes.marginTop}
                    >
                    <SaveButton
                        disabled={miSettingsLoading}
                        variant="contained"
                        color="primary"
                        onClick={postMembershipInvoiceSettings}
                        >
                        Update
                    </SaveButton>
                </Typography>
            </Paper>
        </form>
    )
}
